import { Controller } from '@hotwired/stimulus'
import { format as formatTz } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import '../../../css/associated-person/associated-person.scss'
import {
  trans,
  STATUS_BANNED,
  STATUS_UNVERIFIED,
  DATE_OF_BIRTH_UNKNOWN_SHORT,
  AGE_SHORT,
  AGE_RANGE,
  REPORT_LOADING,
  REPORT_NO_RESULTS,
  REPORT_LOAD_ERROR,
  REPORT_COL_NAME,
  REPORT_COL_DOB,
  REPORT_COL_FLAGS,
  REPORT_COL_INCIDENTS
} from '../../translator'

declare const accountDateFormat: string

interface ApIndicators {
  is_banned: boolean
  prior_incidents_count: number
  has_photo: boolean
}

interface ApTier3Fields {
  status: string
}

interface ApCriminalFlag {
  type: string
  label: string
  severity: string
}

interface ApAgeRange {
  from: number
  to: number
}

interface ApResult {
  id: string
  full_name: string
  date_of_birth: string | null
  date_of_birth_display: string | null
  age_range: ApAgeRange | null
  is_unverified: boolean
  indicators: ApIndicators
  criminal_history_flags: ApCriminalFlag[]
  tier: number
  tier_3_fields: ApTier3Fields | null
}

interface ApiResponse {
  results: ApResult[]
  total_result_count: number
}

interface FilterOption {
  value: string
  label: string
}

interface ReportFilter {
  site_ids?: FilterOption[]
  statuses?: FilterOption[]
  name?: string | null
  is_banned?: boolean
}

/**
 * Renders a live, tier-redacted list of Associated Persons inside a Report.
 *
 * The component root carries the stored ReportField filter JSON in
 * `data-associated-persons-report-filter-value`. On connect the controller
 * queries `GET /internal/api/associated-persons` with the Site, Status and Name
 * filters and renders a single table of every matching record. The Name, DOB,
 * status/unverified indicators and Incident count are shown for every tier; the
 * Flags column renders criminal history flag chips for any tier that has them.
 * The list is rendered fresh per viewer, never snapshotted
 * (AD-10: standalone records are always included server-side).
 */
export default class AssociatedPersonsReportController extends Controller {
  static targets = ['results']
  static values = { filter: String }

  declare readonly filterValue: string
  declare readonly resultsTarget: HTMLElement
  declare readonly hasResultsTarget: boolean

  connect (): void {
    this.load().catch(() => this.renderInto(this.renderError()))
  }

  private async load (): Promise<void> {
    let filter: ReportFilter

    try {
      filter = this.parseFilter()
    } catch {
      this.renderInto(this.renderError())

      return
    }

    this.renderInto(this.renderLoading())

    try {
      const results: ApResult[] = []
      let page = 1
      let totalResultCount = 0

      do {
        const response = await fetchWithHandlingLoginRedirect(this.buildUrl(filter, page))

        if (!response.ok) {
          this.renderInto(this.renderError())

          return
        }

        const data = await response.json() as ApiResponse
        totalResultCount = data.total_result_count

        if (data.results.length === 0) {
          break
        }

        results.push(...data.results)
        page++
      } while (results.length < totalResultCount)

      if (results.length === 0) {
        this.renderInto(this.renderEmpty())

        return
      }

      this.renderInto(this.renderTable(results))
    } catch {
      this.renderInto(this.renderError())
    }
  }

  private parseFilter (): ReportFilter {
    const raw = (this.filterValue ?? '').trim()

    if (raw === '') {
      return {}
    }

    const parsed = JSON.parse(raw) as unknown

    if (typeof parsed !== 'object' || parsed === null) {
      throw new Error('Filter value is not an object')
    }

    return parsed as ReportFilter
  }

  private buildUrl (filter: ReportFilter, page: number): string {
    const params = new URLSearchParams()

    params.set('page', String(page))
    params.set('per_page', '100')

    if (Array.isArray(filter.site_ids)) {
      filter.site_ids.forEach(site => params.append('site_ids[]', site.value))
    }

    if (Array.isArray(filter.statuses)) {
      filter.statuses.forEach(status => params.append('status[]', status.value))
    }

    if (typeof filter.name === 'string' && filter.name.trim() !== '') {
      params.set('query', filter.name.trim())
    }

    if (filter.is_banned === true) {
      params.set('is_banned', 'true')
    }

    return `/internal/api/associated-persons?${params.toString()}`
  }

  private renderInto (html: string): void {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = html
    }
  }

  private renderTable (results: ApResult[]): string {
    return `
      <table class="table align-items-center associated-persons-report__table">
        <thead class="raven-thead">
          <tr>
            <th>${trans(REPORT_COL_NAME, {}, 'associated-persons')}</th>
            <th>${trans(REPORT_COL_DOB, {}, 'associated-persons')}</th>
            <th>${trans(REPORT_COL_FLAGS, {}, 'associated-persons')}</th>
            <th class="text-right">${trans(REPORT_COL_INCIDENTS, {}, 'associated-persons')}</th>
          </tr>
        </thead>
        <tbody>
          ${results.map(ap => this.renderRow(ap)).join('')}
        </tbody>
      </table>
    `
  }

  private renderRow (ap: ApResult): string {
    return `
      <tr data-tier="${escapeHtml(String(ap.tier))}" data-ap-id="${escapeHtml(ap.id)}">
        <td>${this.renderName(ap)}${this.renderIndicators(ap)}</td>
        <td>${this.renderDob(ap.date_of_birth, ap.date_of_birth_display, ap.age_range)}</td>
        <td>${this.renderFlags(ap)}</td>
        <td class="text-right">${ap.indicators.prior_incidents_count}</td>
      </tr>
    `
  }

  private renderName (ap: ApResult): string {
    const name = escapeHtml(ap.full_name)

    if (ap.tier === 3) {
      return `<a href="/associated-persons/${escapeHtml(ap.id)}" class="associated-persons-report__name">${name}</a>`
    }

    return `<span class="associated-persons-report__name">${name}</span>`
  }

  private renderIndicators (ap: ApResult): string {
    const chips: string[] = []

    if (ap.indicators.is_banned) {
      chips.push(`<span class="associated-person-chip associated-person-chip--danger ml-2">${trans(STATUS_BANNED, {}, 'associated-persons')}</span>`)
    }

    if (ap.is_unverified) {
      chips.push(`<span class="associated-person-chip associated-person-chip--warning ml-2">${trans(STATUS_UNVERIFIED, {}, 'associated-persons')}</span>`)
    }

    return chips.join('')
  }

  private renderFlags (ap: ApResult): string {
    return (ap.criminal_history_flags ?? [])
      .map(flag => `<span class="associated-person-chip associated-person-chip--${escapeHtml(flag.severity)}">${escapeHtml(flag.label)}</span>`)
      .join('')
  }

  private renderDob (dob: string | null, dobDisplay: string | null, ageRange: ApAgeRange | null): string {
    let dobText: string

    if (dob !== null) {
      const dateFormat = accountDateFormat.replace('mm', 'MM')
      dobText = escapeHtml(formatTz(parseISO(dob), dateFormat))
    } else if (dobDisplay !== null) {
      dobText = escapeHtml(dobDisplay)
    } else {
      dobText = trans(DATE_OF_BIRTH_UNKNOWN_SHORT, {}, 'associated-persons')
    }

    return dobText + this.renderAgeRange(ageRange)
  }

  private renderAgeRange (ageRange: ApAgeRange | null): string {
    if (ageRange === null) {
      return ''
    }

    const age = ageRange.from === ageRange.to
      ? String(ageRange.from)
      : trans(AGE_RANGE, { '%from%': ageRange.from, '%to%': ageRange.to }, 'associated-persons')

    return ` · ${trans(AGE_SHORT, {}, 'associated-persons')} ${age}`
  }

  private renderLoading (): string {
    return `
      <div class="associated-persons-state py-5 text-center text-muted" role="status" aria-live="polite">
        <div class="spinner-border spinner-border-sm mr-2" aria-hidden="true"></div>
        <span>${trans(REPORT_LOADING, {}, 'associated-persons')}</span>
      </div>
    `
  }

  private renderEmpty (): string {
    return `<div class="associated-persons-state py-5 text-center text-muted">${trans(REPORT_NO_RESULTS, {}, 'associated-persons')}</div>`
  }

  private renderError (): string {
    return `<div class="associated-persons-state py-5 text-center text-danger" role="alert">${trans(REPORT_LOAD_ERROR, {}, 'associated-persons')}</div>`
  }
}
